import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { apiErrorToast } from '@/lib/apiErrorToast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  UserPlus, Search, Phone, Mail, MoreHorizontal, ArrowRight, ChevronRight,
  Trash2, Edit, GraduationCap, LayoutGrid, List, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { id: 'inquiry', label: 'Inquiry', color: 'bg-blue-500', light: 'bg-blue-50 border-blue-200 dark:bg-blue-950/20', text: 'text-blue-700' },
  { id: 'trial', label: 'Trial', color: 'bg-amber-500', light: 'bg-amber-50 border-amber-200 dark:bg-amber-950/20', text: 'text-amber-700' },
  { id: 'evaluation', label: 'Evaluation', color: 'bg-purple-500', light: 'bg-purple-50 border-purple-200 dark:bg-purple-950/20', text: 'text-purple-700' },
  { id: 'negotiation', label: 'Fee Negotiation', color: 'bg-orange-500', light: 'bg-orange-50 border-orange-200 dark:bg-orange-950/20', text: 'text-orange-700' },
  { id: 'enrolled', label: 'Enrolled', color: 'bg-emerald-500', light: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20', text: 'text-emerald-700' },
];

const SOURCES = ['Direct', 'Instagram', 'Google', 'Referral', 'WhatsApp', 'Event', 'Website', 'Other'];
const AGE_GROUPS = ['U-6', 'U-8', 'U-10', 'U-12', 'U-14', 'U-16', 'U-18', 'Senior'];

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [viewMode, setViewMode] = useState('kanban');
  const [showAdd, setShowAdd] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', parent_name: '', age_group: '', sport: 'Football', position: '', source: 'Direct', notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const [lRes, sRes] = await Promise.all([api.get('/crm/leads', { params: { search: search || undefined, stage: stageFilter !== 'all' ? stageFilter : undefined } }), api.get('/crm/stats')]);
      setLeads(lRes.data); setStats(sRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, stageFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/crm/leads', form);
      toast.success('Prospect added!');
      setShowAdd(false);
      setForm({ name: '', phone: '', email: '', parent_name: '', age_group: '', sport: 'Football', position: '', source: 'Direct', notes: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await api.put(`/crm/leads/${leadId}`, { stage: newStage });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, stage: newStage } : l));
      toast.success(`Moved to ${STAGES.find(s => s.id === newStage)?.label}`);
    } catch (err) { apiErrorToast(err, 'Failed to add lead'); }
  };

  const handleConvert = async (lead) => {
    try {
      const res = await api.post(`/crm/leads/${lead.id}/convert`, { batch: lead.age_group || '' });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prospect?')) return;
    try { await api.delete(`/crm/leads/${id}`); toast.success('Deleted'); fetchData(); }
    catch (err) { apiErrorToast(err, 'Failed to update lead'); }
  };

  const getStageLeads = (stageId) => leads.filter(l => l.stage === stageId);

  const LeadCard = ({ lead, compact = false }) => {
    const stage = STAGES.find(s => s.id === lead.stage) || STAGES[0];
    return (
      <div className={`p-3 rounded-lg border ${compact ? '' : stage.light} bg-card hover:shadow-md transition-shadow`} data-testid={`lead-${lead.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm text-foreground truncate">{lead.name}</p>
            {lead.parent_name && <p className="text-xs text-muted-foreground">Parent: {lead.parent_name}</p>}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {STAGES.filter(s => s.id !== lead.stage).map(s => (
                <DropdownMenuItem key={s.id} onClick={() => handleStageChange(lead.id, s.id)}>
                  <div className={`h-2 w-2 rounded-full ${s.color} mr-2`} /> Move to {s.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              {lead.stage !== 'enrolled' && (
                <DropdownMenuItem onClick={() => handleConvert(lead)} className="text-emerald-600"><GraduationCap className="h-4 w-4 mr-2" /> Convert to Student</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => handleDelete(lead.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {lead.age_group && <Badge variant="outline" className="text-[10px] h-5">{lead.age_group}</Badge>}
          {lead.sport && <Badge variant="outline" className="text-[10px] h-5">{lead.sport}</Badge>}
          {lead.source && <Badge variant="secondary" className="text-[10px] h-5">{lead.source}</Badge>}
        </div>
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>}
          {lead.email && <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{lead.email}</span>}
        </div>
        {lead.notes && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2 italic">"{lead.notes}"</p>}
        {lead.status === 'converted' && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] mt-2">Converted to Student</Badge>}
      </div>
    );
  };

  if (loading) return (<div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-5 gap-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}</div></div>);

  return (
    <div className="space-y-5" data-testid="crm-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">Student CRM</h1>
          <p className="text-sm text-muted-foreground">{stats?.total || 0} prospects in pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-0.5">
            <Button variant={viewMode === 'kanban' ? 'default' : 'ghost'} size="sm" className="h-7 text-xs rounded-md" onClick={() => setViewMode('kanban')}><LayoutGrid className="h-3.5 w-3.5 mr-1" /> Kanban</Button>
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" className="h-7 text-xs rounded-md" onClick={() => setViewMode('list')}><List className="h-3.5 w-3.5 mr-1" /> List</Button>
          </div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-prospect-btn"><UserPlus className="h-4 w-4 mr-1" /> Add Prospect</Button></DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Add New Prospect</DialogTitle></DialogHeader>
              <form onSubmit={handleAdd} className="space-y-3 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Student name" required /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Phone *</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+91..." required /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="email@..." /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Parent Name</Label><Input value={form.parent_name} onChange={(e) => setForm({...form, parent_name: e.target.value})} placeholder="Guardian" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Age Group</Label><Select value={form.age_group} onValueChange={(v) => setForm({...form, age_group: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{AGE_GROUPS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-1.5"><Label className="text-xs">Sport</Label><Input value={form.sport} onChange={(e) => setForm({...form, sport: e.target.value})} placeholder="Football" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Source</Label><Select value={form.source} onValueChange={(v) => setForm({...form, source: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Position / Interest</Label><Input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} placeholder="Striker, Midfielder..." /></div>
                <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="How did they find us?" rows={2} /></div>
                <Button type="submit" className="w-full">Add Prospect</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Pipeline Stats */}
      {stats && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant={stageFilter === 'all' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setStageFilter('all')}>All ({stats.total})</Button>
          {STAGES.map(s => (
            <Button key={s.id} size="sm" variant={stageFilter === s.id ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setStageFilter(stageFilter === s.id ? 'all' : s.id)}>
              <div className={`h-2 w-2 rounded-full ${s.color} mr-1.5`} />{s.label} ({stats.by_stage?.[s.id] || 0})
            </Button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search prospects..." className="pl-9 h-9" />
      </div>

      {/* KANBAN VIEW */}
      {viewMode === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kanban-board">
          {STAGES.map(stage => {
            const stageLeads = getStageLeads(stage.id);
            return (
              <div key={stage.id} className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <div className={`h-3 w-3 rounded-full ${stage.color}`} />
                    <span className="text-sm font-semibold">{stage.label}</span>
                  </div>
                  <Badge variant="secondary" className="text-[10px] h-5 tabular-nums">{stageLeads.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[200px] p-2 rounded-xl bg-muted/30 border border-dashed border-border">
                  {stageLeads.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-8">No prospects</p>
                  ) : stageLeads.map(lead => (
                    <LeadCard key={lead.id} lead={lead} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <div className="space-y-2">
          {leads.length === 0 ? (
            <Card className="border-dashed"><CardContent className="flex flex-col items-center justify-center py-16">
              <UserPlus className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No prospects yet</h3>
              <p className="text-sm text-muted-foreground">Click "Add Prospect" to start building your pipeline</p>
            </CardContent></Card>
          ) : leads.map(lead => (
            <LeadCard key={lead.id} lead={lead} compact />
          ))}
        </div>
      )}
    </div>
  );
}
